import { cva } from 'class-variance-authority';
import Link, { LinkProps } from 'next/link';
import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import Plus from './svg/Plus';

export enum ButtonTheme {
  primary = 'primary',
  secondary = 'secondary',
  action = 'action'
}


export enum ButtonColor {
  white = 'white',
  purple = 'purple',
  transparent = 'transparent',
}

export enum ButtonSize {
  sm = 'sm',
  md = 'md',
  lg = 'lg',
}


const button = cva<{
  theme: { [type in ButtonTheme]: string[] };
  color: { [type in ButtonColor]?: string[] };
  size: { [type in ButtonSize]: string[] };
}>(
  [
    'cursor-pointer whitespace-nowrap select-none',
    'flex items-center justify-center',
    'rounded-[46px]',
    'min-w-[190px]',
    'px-[14px]',
    'gap-x-[28px]',
    'stroke-white'
  ],
  {
    variants: {
      theme: {
        [ButtonTheme.primary]: [
          'rounded-tr-none rounded-tl-none tablet:rounded-tr-[40px] tablet:rounded-tl-[40px] [&>*]:text-secondary-400 stroke:secondary-400 hover:bg-secondary-300 [&>*]:hover:text-white hover:stroke-white dark:bg-night-100 dark:[&>*]:text-white dark:stroke-white dark:border-b-night-200 dark:hover:border-b-secondary-400 border-b-solid border-b-secondary-300 dark:border-b-white border-b-[4px] text-secondary-400 stroke-secondary-400 w-full tablet:w-[520px]',
        ],
        [ButtonTheme.secondary]: [
          'w-fit text-[16px] tablet:text-[18px] min-w-[130px] bg-transparent border-solid border-secondary-400 border-[2px] bg-transparent !pb-0 text-primary-300 stroke-primary-300 [&>*]:hover:text-white hover:stroke-white hover:bg-secondary-400 dark:[&>*]:text-white dark:stroke-white dark:hover:bg-secondary-400 dark:hover:text-primary-300 dark:hover:stroke-primary-300 dark:hover:text-white dark:hover:stroke-white px-[35px]'
        ],
        [ButtonTheme.action]: [
          'shadow-actionBtn bg-secondary-400 [&>*]:text-white stroke-white dark:bg-secondary-400 dark:bg-white dark:stroke-secondary-400 dark:[&>*]:text-secondary-400 hover:bg-secondary-300 dark:hover:bg-secondary-300 dark:[&>*]:hover:text-white dark:hover:stroke-white'
        ]
      },
      color: {
        [ButtonColor.white]: ['bg-white'],
        [ButtonColor.purple]: ['bg-secondary-300'],
        [ButtonColor.transparent]: ['bg -transparent']
      },
      size: {
        [ButtonSize.sm]: ['h-[30px] string-12'],
        [ButtonSize.md]: ['h-[42px] string-16'],
        [ButtonSize.lg]: ['h-[48px] string-16'],
      }
    },
    defaultVariants: {
      theme: ButtonTheme.primary,
      color: ButtonColor.transparent,
      size: ButtonSize.lg
    },
  });

interface Props {
  title: string;
  theme: ButtonTheme;
  color?: ButtonColor;
  size?: ButtonSize;
  showIcon?: boolean;
  href?: string;
  linkProps?: Partial<LinkProps>;
  onClick?: () => void;
  className?: string;
  locale: string;
}

export default function Button({ theme, color, showIcon, title, size, className, href, linkProps, locale, onClick }: Props) {
  const cn = twMerge(button({ theme, color, size }), 'flex items-center justify-center gap-x-[24px]', className);

  const Comp = () => {
    const Content = () => (
      <>
        {showIcon ? <Plus className='flex-shrink-0 w-[16px] h-[16px]' /> : null}
        <span>{title}</span>
      </>
    )
    if (href) {
      return (
        <Link hrefLang={locale} title={title} href={href} className={cn} {...linkProps}>
          <Content />
        </Link>
      )
    } else {
      return (
        <div onClick={onClick} className={cn}>
          <Content />
        </div>
      )
    }
  }

  return <Comp />;
}